import React from 'react'
import styled from '@emotion/styled'
import R from 'ramda'
import { fromNullable, Just, Nothing } from 'data.maybe'
import moment from 'moment'
import numeral from 'numeral'
import { Icon, message } from 'antd'
import { getAuthorization, getCredentials } from 'api/pgads'
import { Resizable } from 'react-resizable'
import { CaretUpFilled, CaretDownFilled } from '@ant-design/icons'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'

export const emptyFunction = () => null
export const emptyArray = []
export const empty = {}
export const identity = _ => _

export const determineScrollTo = ({
  unitScrollMils = 0,
  positionScrollMils = 0,
  scrollToUnit,
  scrollToRow,
  scrollToColumn,
  data,
}) => {
  if (unitScrollMils > positionScrollMils) {
    return {
      sRow: getFirstRowOfUnit(data, scrollToUnit),
      sCol: undefined,
      sAlgn: 'start',
    }
  }

  if (positionScrollMils > unitScrollMils) {
    return {
      sRow: scrollToRow,
      sCol: scrollToColumn,
      sAlgn: 'auto',
    }
  }
  return {}
}

/* eslint-disable no-console */
const printValidationErrors = error => validations => {
  console.log('Validation Errors:', error.errorMsg)
  for (let i = 0; i < validations.length; i++) console.log(validations[i])
}
const printStackTrace = error => stackTrace => {
  console.log('System Error:', error.errorMsg)
  console.log(stackTrace)
}

export const errorLogger = err => {
  if (!R.isNil(err.restfulStatus)) {
    const { validationError, stackTrace } = err
    const validationMaybe = fromEmpty(validationError)
    const systemMaybe = fromEmpty(stackTrace)
    validationMaybe.map(printValidationErrors(err))
    systemMaybe.map(printStackTrace(err))
    if (validationMaybe.isNothing && systemMaybe.isNothing) {
      console.log('system error', err)
    }
  } else {
    if (typeof err === 'string') {
      console.log('msg error recieved', err)
    } else {
      console.log('unknown error recieved:', err.then, err, err.stack)
    }
  }
}
export const debugMapper = prefix => data => {
  console.log(prefix, ':', data)
  return data
}
/* eslint-enable no-console */

export const sortUnits = R.sort((a, b) => a.unitName.localeCompare(b.unitName))

export const fromEmpty = val =>
  fromNullable(val).chain(v => (R.isEmpty(v) ? Nothing() : Just(v)))

const unitFuzzyFilter = (fuzzyStr, unitData) =>
  fromEmpty(fuzzyStr)
    .map(debugMapper('fuzzy str'))
    .map(str =>
      unitData.filter(u =>
        u.unitName.toLocaleLowerCase().includes(str.toLocaleLowerCase())
      )
    )

export const filterUnits = fuzzyStr => unitData =>
  unitFuzzyFilter(fuzzyStr, unitData).getOrElse(unitData)

export const createUnitNameMap = R.compose(
  R.fromPairs,
  R.map(u => [u.unitId, u.unitName])
)

export const createUnitHeIndex = hes =>
  R.compose(
    R.chain(u => hes.map(he => ({ unitId: u.unitId, he }))),
    sortUnits
  )

export const transposeSegments = ({ segments, ...rest }) => {
  const row = { ...rest }
  segments.map((seg, i) => {
    row[`mw${i + 1}`] = seg.mw
    row[`price${i + 1}`] = seg.price
  })
  return row
}

export const sortByProfile = (a, b) =>
  a.profile && b.profile
    ? a.profile.localeCompare(b.profile)
    : !a.profile && !b.profile
    ? 0
    : a.profile
    ? 1
    : -1

export const createUnitNameSorter = units => {
  const unitNameMap = createUnitNameMap(units)
  return (a, b) => {
    const aName = unitNameMap[a.unitId]
    const bName = unitNameMap[b.unitId]
    if (R.isNil(aName) && !R.isNil(bName)) return -1
    if (!R.isNil(aName) && R.isNil(bName)) return 1
    if (R.isNil(aName) && R.isNil(bName)) return 0
    return aName.localeCompare(bName)
  }
}
export const sortByScheduleId = (a, b) => a.scheduleId - b.scheduleId

export const composeSorter = (...args) => (a, b) => {
  for (let i = 0; i < args.length; i++) {
    const res = args[i](a, b)
    if (res !== 0) return res
  }
  return 0
}

export const getFirstRowOfUnit = (data, unitId) =>
  fromEmpty(unitId)
    .map(() => R.findIndex(d => d.unitId === unitId, data) + 1)
    .getOrElse(undefined)

// TODO: group
export const findFilteredUnits = ({ fuzzyStr, unitNameMap, unitsInGroup }) => {
  console.log('fitler units in group', unitsInGroup)

  const loweredFuzzy = fromEmpty(fuzzyStr)
    .chain(s => (s.trim().length === 0 ? Nothing() : Just(s)))
    .map(f => f.toLowerCase())
    .getOrElse(null)

  return R.compose(
    R.filter(u => R.isNil(unitsInGroup) || unitsInGroup.includes(u)),
    R.map(([uid, _]) => uid),
    R.filter(
      ([_, name]) =>
        R.isNil(loweredFuzzy) || name.toLowerCase().includes(loweredFuzzy)
    ),
    R.toPairs
  )(unitNameMap)
}

export const convertSubmitInfo = ({ unitId, scheduleId, he }) =>
  R.compose(
    R.map(([type, info]) => ({ unitId, scheduleId, he, type, ...info })),
    R.filter(([_, info]) => info && !R.isNil(info.submitTransId)),
    R.toPairs
  )
export const collectSubmissionInfo = R.chain(({ submitInfo, ...rest }) =>
  convertSubmitInfo(rest)(submitInfo)
)
export const getData = state => state.data.rawData

export const formatNumber = format => num =>
  fromNullable(num)
    .chain(n => (isNaN(n) || n === '' ? Nothing() : Just(n)))
    .map(n => numeral(n).format(format))
    .getOrElse('')

export const dollarFormatter = formatNumber('$0,0.00')

export const booleanFormater = s =>
  R.isNil(s)
    ? ''
    : typeof s === 'boolean' && s
    ? 'Y'
    : typeof s === 'string' && (s === 'true' || s === 'Y' || s === 'y')
    ? 'Y'
    : 'N'

export const createContextAction = handler => props => (e, data) => {
  const { gridStore, gridTools } = data
  const selectedData = gridTools.getSelectedRows(gridStore.getState())
  if (selectedData.length > 0) {
    handler({ ...data, ...props, selectedData })
  }
}

export const fixBoolean = b =>
  R.isNil(b)
    ? undefined
    : typeof b === 'boolean'
    ? b
    : //
      typeof b === 'string' &&
      (b.toLowerCase() === 'y' || b.toLowerCase() === 'true')

export const unitToOption = ({ unitId, unitName }) => ({
  key: unitId,
  value: unitId,
  text: unitName,
})

const isTrue = v =>
  v === true || (typeof v === 'string' && v.toLowerCase() === 'true')

const boolFormatter = ({ trueVal = 'Y', falseVal = 'N' }) => v =>
  R.isNil(v) ? '' : isTrue(v) ? trueVal : falseVal
export const yesNoFormatter = boolFormatter({})

export const createMapEditRow = ({ date }) => data => {
  const submitInfo = { submitStatus: '0' }

  if (Object.keys(data).includes('effectiveDate') && moment.isMoment(date)) {
    return {
      ...data,
      effectiveDate: date.format('YYYY-MM-DD'),
      submitInfo: data.submitInfo ? submitInfo : undefined,
    }
  }

  if (Object.keys(data).includes('transDate') && moment.isMoment(date)) {
    return {
      ...data,
      transDate: date.format('YYYY-MM-DD'),
      submitInfo: data.submitInfo ? submitInfo : undefined,
    }
  }

  return data
}

const ColHeader = styled.div`
  flex: 0 0 ${props => props.width}px;
  width: ${props => props.width}px;
  height: ${props => props.height || 30}px;
  max-width: ${props => props.width}px;
  border-right: 1px solid #ccc;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  user-select: none;
  cursor: ${props => (props.sortable ? 'pointer' : 'default')};
  background-color: ${props => props.backgroundColor || 'steelblue'};
  color: ${props => props.color || 'white'};
  line-height: 1.3em;
  font-weight: ${props => props.fontWeight || 'bold'};
  font-size: ${props => props.fontSize || '0.85em'};
  padding-left: 0.5em;
  padding-right: 0.5em;
  padding-top: 0.3em;
  padding-bottom: 0.3em;
  &:first-of-type {
    border-left: 1px solid #ccc;
    border-top-left-radius: 3px;
  }
  &:last-child {
    ${props => !props.scrollY && 'border-top-right-radius: 3px;'};
  }
`
export const flexHeaderRender = props => {
  // eslint-disable-next-line no-unused-vars
  const { header, width } = props
  return (
    <ColHeader
      {...header}
      fontWeight="normal"
      // backgroundColor={ headers[columnIndex].rowHeaderBackgroundColor || 'steelblue' }
      backgroundColor={header.rowHeaderBackgroundColor || '#eee'}
      // width={width}
      // color="#fff"
      color="#333"
    >
      {header.isKey &&
      header.ident !== 'modifier' &&
      header.ident !== 'modifiedDate'
        ? `${header.display}*`
        : header.display}
    </ColHeader>
  )
}

export const rowHeaderRender = props => {
  const { gridToolProps, reactVirtualizedProps } = props
  const { getColumnHeaderProps, headers } = gridToolProps
  const { columnIndex, style } = reactVirtualizedProps
  const headerProps = getColumnHeaderProps({
    index: columnIndex,
    header: headers[columnIndex],
    style,
  })
  return (
    <ColHeader
      {...headerProps}
      fontWeight="normal"
      // backgroundColor={ headers[columnIndex].rowHeaderBackgroundColor || 'steelblue' }
      backgroundColor={headers[columnIndex].rowHeaderBackgroundColor || '#eee'}
      width={style.width}
      // color="#fff"
      color="#333"
    >
      {headerProps.header.isKey &&
      headerProps.header.ident !== 'modifier' &&
      headerProps.header.ident !== 'modifiedDate'
        ? `${headers[columnIndex].display}*`
        : headers[columnIndex].display}
      {headerProps.sortOrder === 'asc' && <Icon type="caret-up" />}
      {headerProps.sortOrder === 'desc' && <Icon type="caret-down" />}
    </ColHeader>
  )
}

export const resizableHeaderRender = onResize => props => {
  const { gridToolProps, reactVirtualizedProps } = props
  const { getColumnHeaderProps, headers } = gridToolProps
  const { columnIndex, style } = reactVirtualizedProps
  const headerProps = getColumnHeaderProps({
    index: columnIndex,
    header: headers[columnIndex],
    style,
  })
  return (
    <Resizable
      width={style.width}
      height={0}
      onResize={onResize(columnIndex)}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <ColHeader
        {...headerProps}
        fontWeight="normal"
        backgroundColor={
          headers[columnIndex].rowHeaderBackgroundColor || '#D1EEEE'
        }
        width={style.width}
        color="#333"
      >
        {headerProps.header.isKey
          ? headers[columnIndex].display + '*'
          : headers[columnIndex].display}
        {headerProps.sortOrder === 'asc' && <CaretUpFilled />}
        {headerProps.sortOrder === 'desc' && <CaretDownFilled />}
      </ColHeader>
    </Resizable>
  )
}

export const rowHeaderRenderOld = props => {
  const { gridToolProps, reactVirtualizedProps } = props
  const { getColumnHeaderProps, headers } = gridToolProps
  const { columnIndex, style } = reactVirtualizedProps
  return (
    <ColHeader
      {...getColumnHeaderProps({
        index: columnIndex,
        header: headers[columnIndex],
        style,
      })}
      fontWeight="normal"
      backgroundColor={headers[columnIndex].rowHeaderBackgroundColor || '#eee'}
      width={style.width}
      color="#333"
    >
      {headers[columnIndex].display}
    </ColHeader>
  )
}

export const filterMultiSelectItem = ({
  data,
  showAll,
  fuzzyFilter,
  acceptFilter,
  selectedIds,
  toId,
}) =>
  data
    .filter(item => showAll || selectedIds.includes(toId(item)))
    .filter(
      item =>
        acceptFilter == null ||
        fuzzyFilter == null ||
        fuzzyFilter.trim().length === 0 ||
        acceptFilter(item)(fuzzyFilter)
    )
export const enableBatching = reducer => (state, action) => {
  switch (action.type) {
    case 'BATCH_ACTIONS':
      return action.actions.reduce(reducer, state)
    default:
      return reducer(state, action)
  }
}

export const matchAnyRole = roles => R.any(r => R.contains(r, roles))
export const matchAllRoles = roles => appRoles =>
  roles.reduce((a, b) => a && appRoles.includes(b), appRoles.includes(roles[0]))

export const hasAccesPermits = ({
  actionPermits = [],
  eventActions = [],
  matchAny = false,
  authGodMode = false,
}) => {
  if (authGodMode) return true
  if (matchAny) return matchAnyRole(eventActions)(actionPermits)
  return matchAllRoles(eventActions)(actionPermits)
}

export const hasWriteAccessPermit = ({ roles, action, unitId }) => {
  return roles
    .filter(r => r.actions.includes(action))
    .map(r => r.resources.unit || [])
    .includes(unitId)
}

export const severity = {
  1: 'Error',
  0: 'Warning',
}

export const SEVERITY_ERROR_COLOR = '#f6c3c3'
export const SEVERITY_WARNING_COLOR = '#f6f6c3'

export const yesNoMap = {
  Yes: 'YES',
  No: 'NO',
}

export const yesNoCAPMap = {
  YES: 'YES',
  NO: 'NO',
}

export const yesNoYNMap = {
  Y: 'Yes',
  N: 'No',
}
export const yesNoListMap = [
  { key: 'Y', value: 'YES' },
  { key: 'N', value: 'NO' },
  { key: '', value: '' },
]

export const timeZones = {
  EST: 'Eastern Time',
  CST: 'Central Time',
  MST: 'Mountain Time',
  PST: 'Pacific Time',
}

export const timeZoneIdentMap = {
  EST: 'US/Eastern',
  CST: 'US/Central',
  MST: 'MST',
  PST: 'US/Pacific',
  EDT: 'US/Eastern',
  CDT: 'US/Central',
  MDT: 'MST',
  PDT: 'US/Pacific',
  EPT: 'US/Eastern',
  CPT: 'US/Central',
  MPT: 'US/Mountain',
  PPT: 'US/Pacific',
  EST2: 'EST',
  CST2: 'CST',
  MST2: 'MST',
  PST2: 'PST',
}

// this is no longer used
// export const getCauseCodeOmcMap = (causeCodeList = []) => {
//   const cmap = causeCodeList.map(c => ({ [c.causeCode]: c.isOMC }))
//   if (cmap.length > 0) return cmap.reduce((a, b) => ({ ...a, ...b }))
//   return {}
// }

export const exportData = (blob, type, fileName) => {
  const a = window.document.createElement('a')
  a.href = window.URL.createObjectURL(blob, { type })
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const downloadFile = targetInfo => url => fileName => {
  const URL = `${targetInfo.baseUrl}/${url}`
  return fetch(`${URL}`, {
    method: 'GET',
    ignoreAuth: true,
    ...getCredentials(targetInfo),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/octet-stream',
      // jwt: targetInfo.jwt == undefined ? null : targetInfo.jwt,
      ...getAuthorization(targetInfo),
    },
    // body:JSON.stringify(data)
  })
    .then(response => {
      if (response.ok) return response
      else throw new Error('Something went wrong')
    })
    .then(response => response.blob())
    .then(blob =>
      exportData(blob, 'application/pdf', decodeURIComponent(fileName))
    )
    .catch(error => message.error('Error in Reports ' + error))
}

// eslint-disable-next-line no-unused-vars
export const exportDataComponent = (ref, data, filText) => {
  if (ref.current) return ref.current.generateViewProps().view
  return data
}

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const monthsMap = {
  '1': 'January',
  '2': 'February',
  '3': 'March',
  '4': 'April',
  '5': 'May',
  '6': 'June',
  '7': 'July',
  '8': 'August',
  '9': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December',
}

export const monthMapChoice = {
  ...toSelectionColProps(monthsMap),
  choices: [
    { value: '1', text: 'January' },
    { value: '2', text: 'February' },
    { value: '3', text: 'March' },
    { value: '4', text: 'April' },
    { value: '5', text: 'May' },
    { value: '6', text: 'June' },
    { value: '7', text: 'July' },
    { value: '8', text: 'August' },
    { value: '9', text: 'September' },
    { value: '10', text: 'October' },
    { value: '11', text: 'November' },
    { value: '12', text: 'December' },
  ],
}

export const monthsMapWithId = {
  '1': '1-January',
  '2': '2-February',
  '3': '3-March',
  '4': '4-April',
  '5': '5-May',
  '6': '6-June',
  '7': '7-July',
  '8': '8-August',
  '9': '9-September',
  '10': '10-October',
  '11': '11-November',
  '12': '12-December',
}

export const monthMapChoiceWithId = {
  ...toSelectionColProps(monthsMapWithId),
  choices: [
    { value: '1', text: '1-January' },
    { value: '2', text: '2-February' },
    { value: '3', text: '3-March' },
    { value: '4', text: '4-April' },
    { value: '5', text: '5-May' },
    { value: '6', text: '6-June' },
    { value: '7', text: '7-July' },
    { value: '8', text: '8-August' },
    { value: '9', text: '9-September' },
    { value: '10', text: '10-October' },
    { value: '11', text: '11-November' },
    { value: '12', text: '12-December' },
  ],
}

export const DayMap = {
  '1': 'Sunday',
  '2': 'Monday',
  '3': 'Tuesday',
  '4': 'Wednesday',
  '5': 'Thursday',
  '6': 'Friday',
  '7': 'Saturday',
}

export const DayMapSelectionChoice = [
  { value: '1', text: 'Sunday' },
  { value: '2', text: 'Monday' },
  { value: '3', text: 'Tuesday' },
  { value: '4', text: 'Wednesday' },
  { value: '5', text: 'Thursday' },
  { value: '6', text: 'Friday' },
  { value: '7', text: 'Saturday' },
]

export const DayMapSelectionChoiceMap = {
  '1': 'Sunday',
  '2': 'Monday',
  '3': 'Tuesday',
  '4': 'Wednesday',
  '5': 'Thursday',
  '6': 'Friday',
  '7': 'Saturday',
}

export const HOURS = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
  22,
  23,
  24,
].map(h => '' + h)

export const HOURS_CHOICES = HOURS.map(h => ({ text: h, value: h }))

export const sortStringArray = strArray =>
  R.sort(
    (a, b) =>
      (a || '')
        .toLocaleLowerCase()
        .localeCompare((b || '').toLocaleLowerCase()),
    strArray
  )

export const isValidYearRange = (
  yearRange,
  startDate,
  endDate,
  showMessage = true
) => {
  const isValid = endDate.diff(startDate, 'years') <= parseInt(yearRange || 10)
  if (!isValid && showMessage) {
    message.error(
      'Select date range should not have more than a ' +
        yearRange +
        '-year gap.'
    )
  }
  return isValid
}

export const isDifferentErrorValidate = (newError = {}, oldError = {}) => {
  const curError = newError === undefined || newError == null ? {} : newError
  const {
    errorMsg = '',
    httpStatus = '',
    restfulStatus = '',
    // eslint-disable-next-line no-unused-vars
    stackTrace = '',
    targetUrl = '',
    validationError = [],
  } = oldError || {}

  const isDifferent = !(
    errorMsg === (curError.errorMsg || '') &&
    httpStatus === (curError.httpStatus || '') &&
    restfulStatus === (curError.restfulStatus || '') &&
    // stackTrace === curError.stackTrace && // this is different always bcoz of uuid
    targetUrl === (curError.targetUrl || '') &&
    (validationError || []).length === (curError.validationError || []).length
  )

  return isDifferent
}

export const getUnitCodeCauseCodeMapList = causeCodeList => {
  return R.pipe(
    R.groupBy(r => r.unitCode),
    R.map(R.groupBy(r => r.system)),
    R.map(R.map(R.groupBy(r => r.component)))
  )(causeCodeList)
}
export const dataSavedMessage = _ => message.info('Data Saved')
